/**
 * Rx-02..Rx-06 — pharmacist order fulfilment workflow.
 *
 * Claim → confirm address (exact phrase) → per-line availability + pricing → delivery charge
 * → publish (with sign-off last-4 for H/H1/X). Totals update live as the pharmacist types
 * (lib/pricing.ts); publishing sends every line, the delivery charge and the total shown in one
 * request and is accepted only if the server computes the same total (PHA-04). Warnings
 * (first-order / zero-purchase / address-changed) show in the patient card.
 * Only the pharmacist who claimed the order sees the steps; others can ask to take it over, and the
 * claimant can release it (ClaimPanel, PHA-03). The claimant can correct the delivery address until
 * the price is published, and edit a published price until the patient pays (PHA-21, D-23); anyone
 * can download the Rx PDF and the item list.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  cancelPriceEdit,
  confirmAddress,
  deliverOrder,
  dispatchOrder,
  downloadOrderItems,
  downloadRxPdf,
  getPharmacistOrder,
  markReadyForDispatch,
  publishPrice,
  setLineAvailability,
  startPreparing,
  startPriceEdit,
} from "../../api/orders";
import type { OrderLine, PharmacistOrder } from "../../api/orders";
import { ApiError, describeError } from "../../api/errors";
import { useAuth } from "../../auth/AuthContext";
import { isBlank } from "../../lib/form";
import { formatINR } from "../../lib/money";
import {
  deliveryProblem,
  draftFor,
  lineGst,
  linePricingBody,
  lineProblem,
  paiseToString,
  previewPricing,
} from "../../lib/pricing";
import type { LineDraft } from "../../lib/pricing";
import { formatCountdown, useCountdown } from "../../lib/useCountdown";
import { AddressCorrection } from "./AddressCorrection";
import { ClaimPanel } from "./ClaimPanel";
import { OrderTimeline, PatientCard } from "./OrderInfoPanel";

const SIGNOFF_CLASSES = new Set(["H", "H1", "X"]);

// Pricing inputs decide what the patient pays: large, legible digits and a clear focus ring.
const PRICE_INPUT =
  "rounded-lg border border-gray-300 bg-white px-3 py-2 text-base font-medium tabular-nums text-ink " +
  "focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:bg-gray-50 disabled:text-gray-500";

export function PharmacistOrderDetail() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const refetch = () => qc.invalidateQueries({ queryKey: ["ph-order", id] });
  const { user } = useAuth();
  const { data: order, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ["ph-order", id],
    queryFn: () => getPharmacistOrder(id),
  });

  const [phrase, setPhrase] = useState("");
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressRevised, setAddressRevised] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = useMutation({
    mutationFn: () => confirmAddress(id, phrase),
    onSuccess: refetch,
    onError: (e) => setError(e instanceof ApiError && e.code === "ADDRESS_PHRASE_MISMATCH" ? 'Type exactly: "address confirmed"' : describeError(e)),
  });

  if (isLoading) return <p className="text-sm text-gray-400">Loading…</p>;
  if (isError || !order) return <p className="text-sm text-red-600">Order not found.</p>;

  const claimed = order.assignee_pharmacist_id !== null;
  // The API refuses anyone but the claimant (NOT_ASSIGNEE), so only they see the steps (PHA-03).
  const mine = !!user && order.assignee_pharmacist_id === user.id;
  const addressConfirmed = order.address_confirmed_at !== null;
  const published = order.pricing_published_at !== null;
  const paid = order.paid_at !== null;
  // As the API allows (ADDRESS_LOCKED after): until the price is published, confirmed or not (D-23).
  const canCorrectAddress = mine && !published && order.cancelled_at === null;

  const addressCorrection = editingAddress ? (
    <AddressCorrection
      order={order}
      onCancel={() => setEditingAddress(false)}
      onSaved={() => {
        setEditingAddress(false);
        setAddressRevised(true);
        setPhrase("");
        void refetch();
      }}
    />
  ) : (
    <button
      type="button"
      onClick={() => { setError(null); setEditingAddress(true); }}
      className="mt-2 text-sm font-medium text-brand hover:underline"
    >
      Address not right? Correct it
    </button>
  );

  return (
    <div className="max-w-7xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to="/staff/pharmacist/queue" className="text-sm text-brand hover:underline">
          ← Back to queue
        </Link>
        <Downloads order={order} />
      </div>

      {/* Two columns from lg up: who/what/when on the left, the work on the right. */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(17rem,24rem)_minmax(0,1fr)]">
        <div className="space-y-5">
          <PatientCard order={order} />
          {claimed && <OrderTimeline order={order} />}
        </div>

        <div className="min-w-0 space-y-5">
          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          {/* Step 1 — claim, ask to take over, or release */}
          <ClaimPanel order={order} userId={user?.id ?? null} fetchedAt={dataUpdatedAt} onChange={refetch} />

          {/* Step 2 — confirm address */}
          {mine && !addressConfirmed && !published && (
            <Step n={2} title="Confirm address">
              <p className="text-sm text-gray-500">
                Call the patient to confirm the delivery address, then type{" "}
                <span className="font-medium text-gray-700">{ADDRESS_PHRASE}</span> to unlock pricing.
              </p>
              <AddressOnFile order={order} />
              {addressRevised && !editingAddress && (
                <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  Address updated, so it has to be confirmed again. Read the new address back to the patient, then
                  confirm it below.
                </p>
              )}
              {canCorrectAddress && addressCorrection}
              <form
                className="mt-2 flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!phraseMatches(phrase) || confirm.isPending) return;
                  setError(null);
                  confirm.mutate();
                }}
              >
                <PhraseInput phrase={ADDRESS_PHRASE} value={phrase} onChange={setPhrase} disabled={confirm.isPending} />
                <button
                  type="submit"
                  disabled={confirm.isPending || !phraseMatches(phrase)}
                  className="rounded-lg bg-brand px-4 py-1.5 text-sm font-medium text-brand-fg disabled:opacity-50"
                >
                  {confirm.isPending ? "Confirming…" : "Confirm"}
                </button>
              </form>
            </Step>
          )}

          {/* Step 3 — price lines → calculate total → publish */}
          {mine && addressConfirmed && (
            <Step n={3} title="Price & publish" done={published}>
              {canCorrectAddress && (
                // The address can still change before publishing; it then has to be confirmed again (D-23).
                <div className="mb-3 border-b pb-3 text-sm text-gray-500">
                  Address confirmed. Correcting it means confirming it again, and clears the prices typed below.
                  <div>{addressCorrection}</div>
                </div>
              )}
              <PricingStep order={order} onChange={refetch} />
            </Step>
          )}

          {/* Step 4 — fulfilment (after payment) */}
          {paid && mine && (
            <Step n={4} title="Fulfilment" done={order.status_code === "delivered"}>
              <FulfilmentStep
                orderId={id}
                statusCode={order.status_code}
                statusLabel={order.status_label}
                held={order.cancel_request?.status === "open"}
                onAdvance={refetch}
              />
            </Step>
          )}
        </div>
      </div>
    </div>
  );
}

const DOWNLOAD_FORMATS = [
  { format: "xlsx", label: "Excel", description: "Excel (.xlsx)" },
  { format: "pdf", label: "PDF", description: "PDF" },
] as const;

type DownloadKind = "rx" | (typeof DOWNLOAD_FORMATS)[number]["format"];

/** The prescription as a PDF (D-23), and the order's medicine list as a file to share; prices are
 *  included once published. */
function Downloads({ order }: { order: PharmacistOrder }) {
  const download = useMutation({
    mutationFn: (what: DownloadKind) =>
      what === "rx" ? downloadRxPdf(order.id, order.order_id) : downloadOrderItems(order.id, order.order_id, what),
    onError: (e) => toast.error(describeError(e)),
  });
  const button = (what: DownloadKind, label: string, ariaLabel: string) => (
    <button
      key={what}
      type="button"
      onClick={() => download.mutate(what)}
      disabled={download.isPending}
      aria-label={ariaLabel}
      className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink hover:bg-gray-50 disabled:opacity-50"
    >
      <span className="h-4 w-4">
        <DownloadIcon />
      </span>
      {download.isPending && download.variables === what ? "Preparing…" : label}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {button("rx", "Rx PDF", "Download the prescription as a PDF")}
      <div role="group" aria-label="Download prescription items" className="flex items-center gap-2">
        <span className="text-sm text-ink-muted">Download items</span>
        {DOWNLOAD_FORMATS.map(({ format, label, description }) =>
          button(format, label, `Download prescription items as ${description}`),
        )}
      </div>
    </div>
  );
}

/** The address on the order, as it will be read back to the patient. */
function AddressOnFile({ order }: { order: PharmacistOrder }) {
  const a = order.delivery_address_snapshot as Record<string, string | null | undefined>;
  return (
    <div className="mt-3 rounded-lg border border-line bg-gray-50 px-3 py-2 text-sm">
      <div className="text-xs text-ink-muted">Address on the order</div>
      <div className="text-ink">
        {a.name}
        {a.phone ? ` · ${a.phone}` : ""}
      </div>
      <div className="text-ink-soft">
        {a.line1}
        {a.line2 ? `, ${a.line2}` : ""}
        {a.landmark ? ` (near ${a.landmark})` : ""}, {a.city}, {a.state_code} {a.pin_code}
      </div>
    </div>
  );
}

// The phrase that unlocks pricing (backend pricing_service.confirm_address compares it trimmed
// and case-insensitively).
const ADDRESS_PHRASE = "address confirmed";
const phraseMatches = (typed: string) => typed.trim().toLowerCase() === ADDRESS_PHRASE;

/**
 * A typed confirmation phrase. A placeholder vanishes at the first keystroke, so the phrase stays
 * visible as grey "ghost" text after what has been typed, showing what's left. Going off the
 * phrase turns the field red; the exact phrase turns it green.
 */
function PhraseInput({
  phrase,
  value,
  onChange,
  disabled,
}: {
  phrase: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const onTrack = phrase.startsWith(value.toLowerCase());
  const done = phraseMatches(value);
  const state = done ? "border-emerald-500" : onTrack ? "" : "border-red-400";
  return (
    <div className="flex-1">
      <div className="relative rounded-lg bg-white">
        {/* Mirrors the input's text box: the typed part is invisible, so the rest of the phrase
            starts exactly at the caret. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-pre border border-transparent px-3 text-sm">
          <span className="invisible">{value}</span>
          {onTrack && !done && <span className="text-gray-400">{phrase.slice(value.length)}</span>}
        </div>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid="confirm-phrase"
          aria-label={`Type "${phrase}"`}
          aria-invalid={!onTrack && !done}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          className={`relative w-full rounded-lg border bg-transparent px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-brand ${state}`}
        />
      </div>
      {!onTrack && !done && (
        <p className="mt-1 text-xs text-red-600">
          Type exactly: <span className="font-medium">{phrase}</span>
        </p>
      )}
    </div>
  );
}

// The post-payment chain: each status maps to the next action's label + mutation. `delivered` is
// terminal. The backend asserts the current status, so we just drive off status_code.
const FULFILMENT_STAGES = [
  { code: "paid", label: "Start preparing", run: startPreparing },
  { code: "preparing", label: "Mark ready for dispatch", run: markReadyForDispatch },
  { code: "ready_for_dispatch", label: "Mark dispatched", run: dispatchOrder },
  { code: "dispatched", label: "Mark delivered", run: deliverOrder },
];
const FULFILMENT_ORDER = [...FULFILMENT_STAGES.map((s) => s.code), "delivered"];

// How each status shows on the progress trail. The action button reuses the icon of the status it
// moves the order to.
const STAGE_DISPLAY: Record<string, { label: string; icon: React.ReactNode }> = {
  paid: { label: "Paid", icon: <PaidIcon /> },
  preparing: { label: "Preparing", icon: <PreparingIcon /> },
  ready_for_dispatch: { label: "Ready for dispatch", icon: <PackedIcon /> },
  dispatched: { label: "Dispatched", icon: <TruckIcon /> },
  delivered: { label: "Delivered", icon: <DeliveredIcon /> },
};

function FulfilmentStep({
  orderId,
  statusCode,
  statusLabel,
  held,
  onAdvance,
}: {
  orderId: string;
  statusCode: string;
  statusLabel: string;
  /** The patient asked to cancel and an admin is deciding (ORD-16): the order can't move on. */
  held: boolean;
  onAdvance: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const stage = FULFILMENT_STAGES.find((s) => s.code === statusCode);
  const advance = useMutation({
    mutationFn: () => stage!.run(orderId),
    onSuccess: onAdvance,
    onError: (e) => {
      setError(describeError(e));
      onAdvance(); // show the order as it now is (e.g. held, or refunded meanwhile)
    },
  });
  const currentIndex = FULFILMENT_ORDER.indexOf(statusCode);
  const next = currentIndex >= 0 ? FULFILMENT_ORDER[currentIndex + 1] : undefined;

  // A refunded, cancelled, failed or returned order is off the track (PHA-08): payment stays
  // recorded, so the step shows, but it must not read "Order delivered."
  if (currentIndex === -1) {
    return <p className="text-sm text-gray-600">No fulfilment steps: this order is {statusLabel.toLowerCase()}.</p>;
  }

  return (
    <div className="space-y-4">
      {/* Progress trail: done steps green, the current one highlighted, the rest still to come. */}
      <ol aria-label="Fulfilment progress" className="grid grid-cols-5">
        {FULFILMENT_ORDER.map((code, i) => {
          const { label, icon } = STAGE_DISPLAY[code];
          const state = i < currentIndex ? "done" : i === currentIndex ? "current" : "todo";
          const circle =
            state === "done"
              ? "bg-emerald-100 text-emerald-700"
              : state === "current"
                ? code === "delivered"
                  ? "bg-emerald-600 text-white"
                  : "bg-brand text-brand-fg ring-4 ring-brand/15"
                : "border border-gray-200 bg-white text-gray-400";
          return (
            <li
              key={code}
              aria-current={state === "current" ? "step" : undefined}
              className="relative flex flex-col items-center gap-1.5 text-center"
            >
              {/* Connector to the next step, behind the circles; green once that step is reached. */}
              {i < FULFILMENT_ORDER.length - 1 && (
                <span
                  aria-hidden="true"
                  className={`absolute left-1/2 top-[17px] h-0.5 w-full ${i < currentIndex ? "bg-emerald-300" : "bg-gray-200"}`}
                />
              )}
              <span className={`relative grid h-9 w-9 place-items-center rounded-full transition-colors ${circle}`}>
                <span className="h-[18px] w-[18px]">{icon}</span>
              </span>
              <span
                className={`px-0.5 text-[11px] leading-tight sm:px-1 sm:text-xs ${
                  state === "current" ? "font-semibold text-gray-900" : state === "done" ? "text-emerald-700" : "text-gray-400"
                }`}
              >
                {label}
              </span>
              <span className="sr-only">{state === "done" ? "(done)" : state === "current" ? "(current)" : "(to do)"}</span>
            </li>
          );
        })}
      </ol>

      {held && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <span className="font-medium">On hold.</span> The patient has asked to cancel this order; an admin is
          reviewing it. Don't prepare or dispatch it until then.
        </div>
      )}

      {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {stage ? (
        <button
          onClick={() => { setError(null); advance.mutate(); }}
          disabled={advance.isPending || held}
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50"
        >
          {next && <span className="h-4 w-4">{STAGE_DISPLAY[next].icon}</span>}
          {advance.isPending ? "Updating…" : stage.label}
        </button>
      ) : (
        <p className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
          <span className="h-4 w-4">
            <CheckCircleIcon />
          </span>
          Order delivered.
        </p>
      )}
    </div>
  );
}

/* --- fulfilment icons (stroke=currentColor so they take the step's colour) --- */

function Svg({ children }: { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-full w-full"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Paid — a banknote. */
function PaidIcon() {
  return (
    <Svg>
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 10v4M18 10v4" />
    </Svg>
  );
}

/** Preparing — a capsule: the medicines are being picked and packed. */
function PreparingIcon() {
  return (
    <Svg>
      <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
      <path d="m8.5 8.5 7 7" />
    </Svg>
  );
}

/** Ready for dispatch — a sealed parcel. */
function PackedIcon() {
  return (
    <Svg>
      <path d="M21 8 12 3 3 8v8l9 5 9-5Z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
      <path d="m7.5 5.5 9 5" />
    </Svg>
  );
}

/** Dispatched — a delivery truck. */
function TruckIcon() {
  return (
    <Svg>
      <path d="M14 17V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h2" />
      <path d="M14 17H9" />
      <path d="M14 9h4.5l3.5 4.5V16a1 1 0 0 1-1 1h-1" />
      <circle cx="7" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
    </Svg>
  );
}

/** Delivered — home, with a tick. */
function DeliveredIcon() {
  return (
    <Svg>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9" />
      <path d="m9 15 2 2 4-4" />
    </Svg>
  );
}

/** Download — an arrow into a tray. */
function DownloadIcon() {
  return (
    <Svg>
      <path d="M12 4v11" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 20h14" />
    </Svg>
  );
}

function CheckCircleIcon() {
  return (
    <Svg>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </Svg>
  );
}

/**
 * Step 3. The totals update as the pharmacist types: lib/pricing.ts computes them exactly as the
 * server does, from the order's `pricing_basis` (GST rates, same-state or not). Publish sends
 * every line, the delivery charge and the total shown in one request; the server saves them,
 * recomputes, and publishes only if its total matches — otherwise nothing is saved, the order
 * reloads (e.g. a GST rate just changed) and the new total shows for another look.
 *
 * A published, unpaid price can be edited again ("Edit price"): the patient can't pay meanwhile,
 * for at most the server's edit window, counted down here. Nothing is saved while editing —
 * availability included — until "Publish new price" sends it all in that same one request.
 */
function PricingStep({ order, onChange }: { order: PharmacistOrder; onChange: () => Promise<unknown> }) {
  const { user } = useAuth();
  const published = order.pricing_published_at !== null;
  const paid = order.paid_at !== null;
  const awaitingPayment = order.status_code === "awaiting_payment";
  const needsSignoff = SIGNOFF_CLASSES.has(order.schedule_class);
  const paying = order.payment?.status === "pending"; // the patient's Checkout is open (or unreported)

  // A price edit: the server's running revision, and whether it is mine. My edit stays open here
  // until I publish, cancel or discard it — also once its window has lapsed, since the server still
  // accepts the publish then, provided the patient hasn't started paying.
  const revision = order.price_revision ?? null;
  // A server without price editing sends no `price_revision` at all (this one sends null when no
  // edit is open), so "Edit price" is offered only once the backend supports it.
  const canEditPrice = order.price_revision !== undefined;
  const revisionIsMine = revision !== null && revision.started_by_user_id === user?.id;
  const [editId, setEditId] = useState<string | null>(null);
  // The revision I just published or cancelled: until the order reloads, the server's last answer
  // still shows it running, and it must not be taken up again.
  const [closedId, setClosedId] = useState<string | null>(null);
  if (revisionIsMine && revision && editId !== revision.revision_id && closedId !== revision.revision_id) {
    setEditId(revision.revision_id); // e.g. the page was reloaded mid-edit
  }
  const endEdit = () => {
    setClosedId(editId);
    setEditId(null);
  };
  const editing = published && !paid && awaitingPayment && editId !== null;
  const secondsLeft = useCountdown(
    editing ? (revisionIsMine && revision ? revision.seconds_left : 0) : null,
    revision?.expires_at,
  );
  const othersSecondsLeft = useCountdown(revision && !revisionIsMine ? revision.seconds_left : null, revision?.expires_at);

  const savedDrafts = () => Object.fromEntries(order.lines.map((ln) => [ln.id, draftFor(ln)]));
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>(savedDrafts);
  const [availability, setAvailability] = useState<Record<string, boolean>>({});
  const [delivery, setDelivery] = useState(order.delivery_charge);
  const [signoff, setSignoff] = useState("");
  const [error, setError] = useState<string | null>(null);
  const resetDrafts = () => {
    setDrafts(savedDrafts());
    setAvailability({});
    setDelivery(order.delivery_charge);
    setSignoff("");
  };

  const draftOf = (ln: OrderLine) => drafts[ln.id] ?? draftFor(ln);
  const editDraft = (ln: OrderLine, patch: Partial<LineDraft>) =>
    setDrafts((all) => ({ ...all, [ln.id]: { ...(all[ln.id] ?? draftFor(ln)), ...patch } }));
  const availableOf = (ln: OrderLine) => availability[ln.id] ?? ln.is_available;

  // Inputs are live before the first publish and during an edit; otherwise the saved price shows.
  const pricing = !published || editing;
  // In an edit, availability is part of the draft, so the preview counts the lines as set now.
  const previewLines = editing ? order.lines.map((ln) => ({ ...ln, is_available: availableOf(ln) })) : order.lines;

  // A server older than this page sends no pricing_basis; without it nothing can be previewed,
  // so pricing waits for the server rather than crashing the page.
  const basis = order.pricing_basis as PharmacistOrder["pricing_basis"] | undefined;
  const preview = basis ? previewPricing(previewLines, basis, draftOf, delivery) : null;
  const deliveryError = deliveryProblem(delivery);

  const linesBody = () =>
    editing
      ? order.lines.map((ln) =>
          availableOf(ln)
            ? { ...linePricingBody(ln, draftOf(ln)), is_available: true }
            : { line_id: ln.id, quantity_dispensed: 0, quantity_remark: null, mrp_per_unit: "0", is_available: false },
        )
      : order.lines.filter((ln) => ln.is_available).map((ln) => linePricingBody(ln, draftOf(ln)));

  const publish = useMutation({
    mutationFn: () =>
      publishPrice(order.id, {
        schedule_signoff_last4: signoff.trim() || null,
        lines: linesBody(),
        delivery_charge: paiseToString(preview!.delivery),
        expected_final_amount: paiseToString(preview!.total),
      }),
    onSuccess: async () => {
      const wasEditing = editing;
      endEdit();
      setError(null);
      await onChange();
      if (wasEditing) toast.success("New price published — the patient can pay it now.");
    },
    onError: (e) => {
      setError(publishError(e, preview!.total));
      // My edit is over: closed elsewhere (published or cancelled in another tab), or lapsed and
      // another pharmacist opened one (PHA-19). Show the price as it is.
      if (e instanceof ApiError && (e.code === "PRICE_EDIT_REQUIRED" || e.code === "PRICE_EDIT_IN_PROGRESS")) {
        endEdit();
        resetDrafts();
      }
      // Nothing was saved; reload so a changed rate or availability shows in the preview.
      void onChange();
    },
  });

  const startEdit = useMutation({
    mutationFn: () => startPriceEdit(order.id),
    onSuccess: async (updated) => {
      resetDrafts();
      setError(null);
      setEditId(updated.price_revision?.revision_id ?? null);
      await onChange();
    },
    onError: (e) => {
      setError(editError(e));
      void onChange();
    },
  });

  const cancelEdit = useMutation({
    mutationFn: () => cancelPriceEdit(order.id),
    onSuccess: async () => {
      endEdit();
      resetDrafts();
      setError(null);
      await onChange();
    },
    onError: (e) => setError(describeError(e)),
  });

  const busy = publish.isPending || cancelEdit.isPending;
  const publishBlocker = !preview
    ? "This order can't be priced yet: the server didn't send the GST details. Reload once the server is updated."
    : !preview.complete
    ? "Fix the highlighted fields to see the final total."
    : preview.medicines === 0n
      ? "Nothing to charge yet. Price at least one available medicine."
      : null;

  return (
    <div>
      {!published && (
        <p className="mb-3 text-sm text-gray-500">
          Set the quantity and MRP for each medicine and the delivery charge; the total, with GST, updates as you
          type. Publishing saves the prices and sends the total to the patient for payment.
        </p>
      )}

      {editing && (
        <div
          role="status"
          data-testid="price-edit-banner"
          className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <span className="h-5 w-5 shrink-0" aria-hidden="true">
            <ClockIcon />
          </span>
          {secondsLeft !== null && secondsLeft > 0 ? (
            <>
              <span>
                <span className="font-medium">Editing the published price.</span> The patient can't pay until you publish
                the new price or cancel.
              </span>
              <span
                data-testid="price-edit-countdown"
                className="ml-auto font-mono text-lg font-semibold tabular-nums"
                aria-label={`${secondsLeft} seconds left`}
              >
                {formatCountdown(secondsLeft)}
              </span>
            </>
          ) : (
            <span>
              The edit window has ended, so the patient can pay the published {formatINR(order.final_amount)} again. You
              can still publish your changes if they haven't started paying.
            </span>
          )}
        </div>
      )}

      <div className="space-y-2">
        {order.lines.map((ln) => (
          <LineRow
            key={ln.id}
            orderId={order.id}
            line={ln}
            draft={pricing ? draftOf(ln) : draftFor(ln)}
            onDraft={(patch) => editDraft(ln, patch)}
            priced={pricing ? preview?.lines[ln.id] : "saved"}
            disabled={!pricing || busy}
            onChange={onChange}
            availability={
              editing ? { value: availableOf(ln), onChange: (v) => setAvailability((a) => ({ ...a, [ln.id]: v })) } : undefined
            }
          />
        ))}
      </div>

      {pricing && (
        <div className="mt-3">
          <label className="flex flex-col gap-1.5 text-sm text-gray-600">
            Delivery charge (₹)
            <input
              data-testid="delivery-charge"
              value={delivery}
              onChange={(e) => setDelivery(e.target.value)}
              disabled={busy}
              inputMode="decimal"
              aria-invalid={deliveryError !== null}
              className={`w-40 ${PRICE_INPUT} ${deliveryError ? "border-red-400" : ""}`}
            />
          </label>
          {deliveryError && <p className="mt-1 text-xs text-red-600">{deliveryError}</p>}
        </div>
      )}

      {!pricing ? (
        <Totals
          medicines={order.medicines_subtotal}
          delivery={order.delivery_charge}
          gst={order.gst_total}
          total={order.final_amount}
          gstLabel="GST"
        />
      ) : preview && basis ? (
        <Totals
          medicines={paiseToString(preview.medicines)}
          delivery={paiseToString(preview.delivery)}
          gst={paiseToString(preview.gst)}
          total={preview.complete ? paiseToString(preview.total) : null}
          gstLabel={basis.intra_state ? "GST (CGST + SGST)" : "GST (IGST)"}
        />
      ) : null}

      {error && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {pricing && (
        <div className="mt-3 space-y-2 border-t pt-3">
          {needsSignoff && (
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              Sign-off — last 4 of your registration # (Schedule {order.schedule_class})
              <input value={signoff} onChange={(e) => setSignoff(e.target.value)} maxLength={4} className="w-28 rounded-sm border px-2 py-1 text-sm" />
            </label>
          )}
          <p className="text-sm text-gray-600">
            {publishBlocker ??
              (!editing
                ? `The patient will be asked to pay ${formatINR(paiseToString(preview!.total))}.`
                : formatINR(paiseToString(preview!.total)) === formatINR(order.final_amount)
                  ? `No change to the total yet: the patient would still pay ${formatINR(order.final_amount)}.`
                  : `The patient will be asked to pay ${formatINR(paiseToString(preview!.total))} instead of ${formatINR(order.final_amount)}.`)}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setError(null); publish.mutate(); }}
              disabled={busy || publishBlocker !== null || (needsSignoff && isBlank(signoff))}
              className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50"
            >
              {publish.isPending ? "Publishing…" : editing ? "Publish new price" : "Publish price"}
            </button>
            {editing && (
              <button
                onClick={() => { setError(null); cancelEdit.mutate(); }}
                disabled={busy}
                className="rounded-lg border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {cancelEdit.isPending ? "Cancelling…" : "Cancel edit"}
              </button>
            )}
          </div>
        </div>
      )}

      {published && !editing && (
        <div className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
          {paid ? (
            "Payment received." // fulfilment progress is shown in the step below
          ) : !awaitingPayment ? (
            `Price published · order ${order.status_label.toLowerCase()}.`
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>Price published — order is awaiting payment.</span>
                {revision && !revisionIsMine ? (
                  <span className="font-medium text-amber-800">
                    Being edited by {revision.started_by_name ?? "another pharmacist"}
                    {othersSecondsLeft ? ` · ${formatCountdown(othersSecondsLeft)} left` : ""}
                  </span>
                ) : canEditPrice ? (
                  <button
                    onClick={() => { setError(null); startEdit.mutate(); }}
                    disabled={paying || startEdit.isPending}
                    className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                  >
                    {startEdit.isPending ? "Opening…" : "Edit price"}
                  </button>
                ) : null}
              </div>
              {paying && canEditPrice && (
                <p className="mt-2 text-xs text-emerald-900">
                  The patient has started paying, so the price can't change unless that payment is cancelled or fails.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Why an edit couldn't start, in the pharmacist's terms. */
function editError(e: unknown): string {
  if (!(e instanceof ApiError)) return describeError(e);
  switch (e.code) {
    case "PAYMENT_IN_PROGRESS":
      return "The patient has started paying, so the price can't change now. Try again if that payment is cancelled or fails.";
    case "ORDER_ALREADY_PAID":
      return "The patient has just paid this order, so its price can't change any more.";
    case "NOT_ASSIGNEE":
      return "Claim the order before editing its price.";
    case "PRICE_NOT_EDITABLE":
      return "Only a published price awaiting payment can be edited.";
    default:
      return describeError(e); // e.g. PRICE_EDIT_IN_PROGRESS names who is editing
  }
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-full w-full">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** Publish failures in the pharmacist's terms. Nothing was saved in any of these cases. */
function publishError(e: unknown, shownTotal: bigint): string {
  if (!(e instanceof ApiError)) return describeError(e);
  switch (e.code) {
    case "PRICE_MISMATCH": {
      const server = typeof e.details.final_amount === "string" ? formatINR(e.details.final_amount) : "a different total";
      return `Not published: the server calculated ${server}, not ${formatINR(paiseToString(shownTotal))}. A GST rate or pharmacy setting may have just changed. The total below is now up to date; check it and publish again.`;
    }
    case "LINE_UNAVAILABLE":
      return "A medicine's availability changed. The order has been reloaded; check it and publish again.";
    case "LINES_INCOMPLETE":
      // The page checks the same rule (lib/pricing lineProblem), so a line changed elsewhere (PHA-04).
      return "Not published: some available medicines aren't priced. Enter a quantity of at least 1 and an MRP above zero for each (and a remark where you dispense less than prescribed), or mark it not available. The order has been reloaded.";
    case "NOTHING_TO_CHARGE":
      return "Nothing to charge. Price at least one available medicine.";
    case "ADDRESS_NOT_CONFIRMED":
      return "Confirm the address first.";
    case "PAPER_ORIGINAL_NOT_HELD":
      return "Schedule X requires the paper original to be held.";
    case "SIGNOFF_INVALID":
      return "Sign-off (last 4 of your registration #) is required and must match.";
    case "PAYMENT_IN_PROGRESS":
      return "Not published: the patient started paying the current price after your edit window ended. Your changes weren't saved.";
    case "ORDER_ALREADY_PAID":
      return "Not published: the patient has already paid, so the price can't change.";
    case "PRICE_EDIT_REQUIRED":
      return "This edit was already closed (published or cancelled elsewhere). The order has been reloaded.";
    case "PRICE_EDIT_IN_PROGRESS":
      return "Not published: another pharmacist is editing this price now, so your edit was closed and your changes weren't saved.";
    case "PRICE_NOT_EDITABLE":
    case "ORDER_NOT_PRICEABLE":
      return "This order can't be priced in its current state.";
    default:
      return describeError(e);
  }
}

function LineRow({
  orderId,
  line,
  draft,
  onDraft,
  priced,
  disabled,
  onChange,
  availability,
}: {
  orderId: string;
  line: OrderLine;
  draft: LineDraft;
  onDraft: (patch: Partial<LineDraft>) => void;
  /** The live amount for the typed values (null while invalid), or "saved" once published. */
  priced: { taxable: bigint; gst: bigint } | null | undefined | "saved";
  disabled: boolean;
  onChange: () => Promise<unknown>;
  /** During a price edit, availability is a draft sent with the re-publish — not saved at once. */
  availability?: { value: boolean; onChange: (available: boolean) => void };
}) {
  const [error, setError] = useState<string | null>(null);
  const avail = useMutation({
    mutationFn: (v: boolean) => setLineAvailability(orderId, line.id, v),
    onSuccess: onChange,
    onError: (e) => setError(describeError(e)),
  });
  const available = availability ? availability.value : line.is_available;
  const problem = disabled || !available ? null : lineProblem(line, draft);
  const short = Number(draft.qty) < line.quantity_prescribed;

  return (
    <div className={`rounded-lg border p-3 text-sm ${problem ? "border-red-300" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="font-medium">{line.medicine_name}</span>
        <label className="flex items-center gap-1 text-xs text-gray-500">
          <input
            type="checkbox"
            aria-label={`${line.medicine_name} available`}
            checked={available}
            disabled={disabled || avail.isPending}
            onChange={(e) => {
              setError(null);
              if (availability) availability.onChange(e.target.checked);
              else avail.mutate(e.target.checked);
            }}
          />
          Available
        </label>
      </div>
      {available && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <Field label={`Dispense (≤ ${line.quantity_prescribed})`}>
            <input value={draft.qty} onChange={(e) => onDraft({ qty: e.target.value })} disabled={disabled} inputMode="numeric" className={`w-28 ${PRICE_INPUT}`} />
          </Field>
          <Field label="MRP/unit (₹)">
            <input value={draft.mrp} onChange={(e) => onDraft({ mrp: e.target.value })} disabled={disabled} inputMode="decimal" className={`w-40 ${PRICE_INPUT}`} />
          </Field>
          {short && (
            <Field label="Remark (required)">
              <input value={draft.remark} onChange={(e) => onDraft({ remark: e.target.value })} disabled={disabled} className={`w-64 ${PRICE_INPUT}`} />
            </Field>
          )}
          <span className="ml-auto text-right" aria-live="polite">
            {priced === "saved" ? (
              <>
                <span className="text-base font-semibold text-gray-800">{formatINR(line.taxable_value)}</span>
                <span className="block text-xs text-gray-400">+ {formatINR(lineGst(line))} GST</span>
              </>
            ) : priced ? (
              <>
                <span className="text-base font-semibold text-gray-800">{formatINR(paiseToString(priced.taxable))}</span>
                <span className="block text-xs text-gray-400">+ {formatINR(paiseToString(priced.gst))} GST</span>
              </>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </span>
        </div>
      )}
      {(problem || error) && <p className="mt-1 text-xs text-red-600">{error ?? problem}</p>}
    </div>
  );
}

/** The order totals: live while pricing, the server's saved figures once published. A null
 *  `total` (some input is invalid) dims the breakdown and hides the total. */
function Totals({
  medicines,
  delivery,
  gst,
  total,
  gstLabel,
}: {
  medicines: string;
  delivery: string;
  gst: string;
  total: string | null;
  gstLabel: string;
}) {
  return (
    <div className={`mt-3 space-y-1 border-t pt-2 text-sm ${total === null ? "opacity-50" : ""}`} aria-live="polite">
      <Row label="Medicines" value={formatINR(medicines)} />
      <Row label="Delivery" value={formatINR(delivery)} />
      <Row label={gstLabel} value={formatINR(gst)} />
      <div className="flex justify-between pt-1 font-semibold">
        <span>Total payable</span>
        <span data-testid="order-total">{total === null ? "—" : formatINR(total)}</span>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-gray-600">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function Step({ n, title, done, children }: { n: number; title: string; done?: boolean; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-white p-5 shadow-xs">
      <div className="mb-3 flex items-center gap-2">
        <span className={`grid h-6 w-6 place-items-center rounded-full text-xs font-bold ${done ? "bg-emerald-100 text-emerald-700" : "bg-brand text-brand-fg"}`}>
          {done ? "✓" : n}
        </span>
        <h3 className="font-medium">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm text-gray-600">
      {label}
      {children}
    </label>
  );
}
